import React, { useMemo } from 'react';

import {
  EvidenceGroup,
  EvidenceRow,
  LABELS,
  SECTIONS,
  useEvidenceReviewState,
} from '@/components/intakes/evidence-review-dialog';
import type {
  EvidenceStatus,
  EvidenceStatusesWithSubs,
  IntakeFormData,
} from '@/components/intakes/form/types';

/**
 * Props for `InlineReviewPanel` which renders inline evidence review controls.
 */
interface InlineReviewPanelProps {
  form: IntakeFormData;
  initialStatuses: EvidenceStatusesWithSubs;
  onChange?: (statuses: EvidenceStatusesWithSubs) => void;
}

/** Keys of evidence status map used to render review rows. */
type EvidenceKey = keyof IntakeFormData['evidenceStatuses'];

/**
 * Inline panel to review liability, treatment, and coverage evidence within the intake.
 */
export const InlineReviewPanel: React.FC<InlineReviewPanelProps> = ({
  form,
  initialStatuses,
  onChange,
}) => {
  const { statuses, handleChange, insuranceSubs, setInsuranceSubs } =
    useEvidenceReviewState({ open: true, form, initialStatuses });

  // Local helpers to emit on user actions instead of effects
  /**
   * Updates a top-level evidence status and emits the consolidated next state.
   */
  const handleTopLevelChange = (key: EvidenceKey, value: EvidenceStatus) => {
    handleChange(key, value);
    const next: EvidenceStatusesWithSubs = {
      ...(statuses as EvidenceStatusesWithSubs),
      [key]: value,
    };
    onChange?.(next);
  };

  /**
   * Updates one of the coverage sub-statuses and emits the consolidated next state.
   */
  const handleSubsChange = (
    which: 'pip' | 'health' | 'thirdParty',
    value: EvidenceStatus,
  ) => {
    setInsuranceSubs((s) => {
      const nextSubs = { ...s, [which]: value } as typeof s;
      const next: EvidenceStatusesWithSubs = {
        ...(statuses as EvidenceStatusesWithSubs),
        first_party: nextSubs.pip,
        health: nextSubs.health,
        third_party: nextSubs.thirdParty,
      };
      onChange?.(next);
      return nextSubs;
    });
  };

  /**
   * Groupings of evidence keys by review section.
   */
  const sections = useMemo(() => {
    return {
      liability: SECTIONS.liability as ReadonlyArray<EvidenceKey>,
      treatment: SECTIONS.treatment as ReadonlyArray<EvidenceKey>,
      coverage: SECTIONS.coverage as ReadonlyArray<EvidenceKey>,
    };
  }, []);

  /**
   * Strongly typed label map for evidence keys.
   */
  const labelMap: Readonly<Record<EvidenceKey, string>> = LABELS as Record<
    EvidenceKey,
    string
  >;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden min-h-[680px]">
      <div className="px-5 pt-4 pb-2 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="flex-1">
          <h2 className="text-base font-semibold text-gray-900">
            Evidence Review
          </h2>
          <p className="text-sm text-gray-700 mt-1">
            Review the key details. If something’s missing, mark needs follow-up
            or does not exist and add a note.
          </p>
        </div>
      </div>

      <div className="px-5 py-4 space-y-5">
        <EvidenceGroup
          title="Liability"
          keys={sections.liability}
          labels={labelMap}
          statuses={statuses}
          form={form}
          contextPlaceholderFor={() => 'Add context…'}
          onChange={handleTopLevelChange}
        />

        <EvidenceGroup
          title="Treatment"
          keys={sections.treatment}
          labels={labelMap}
          statuses={statuses}
          form={form}
          contextPlaceholderFor={() => 'Add context…'}
          onChange={handleTopLevelChange}
        />

        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Coverage</h3>
          <div className="border border-gray-200 rounded-md bg-white divide-y">
            <EvidenceRow
              id="review-ins-pip"
              title="PIP / First Party"
              value={insuranceSubs.pip}
              onChange={(v) => handleSubsChange('pip', v)}
              contextPlaceholder=""
              hintLines={
                (form.insurance.pipInsurance.companyName?.trim() ||
                  form.insurance.pipInsurance.policyHolderName?.trim() ||
                  form.insurance.pipInsurance.policyNumber?.trim() ||
                  form.insurance.pipInsurance.claimNumber?.trim()) && (
                  <>
                    <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                      {form.insurance.pipInsurance.companyName || 'PIP'}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                      {[
                        form.insurance.pipInsurance.policyHolderName &&
                          `Holder: ${form.insurance.pipInsurance.policyHolderName}`,
                        form.insurance.pipInsurance.policyNumber &&
                          `Policy # ${form.insurance.pipInsurance.policyNumber}`,
                        form.insurance.pipInsurance.claimNumber &&
                          `Claim # ${form.insurance.pipInsurance.claimNumber}`,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </div>
                  </>
                )
              }
            />

            <EvidenceRow
              id="review-ins-health"
              title="Health Insurance"
              value={insuranceSubs.health}
              onChange={(v) => handleSubsChange('health', v)}
              contextPlaceholder=""
              hintLines={
                <>
                  {form.insurance.healthProvider?.trim() && (
                    <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                      {form.insurance.healthProvider}
                    </div>
                  )}
                  {(form.insurance.healthMemberID?.trim() ||
                    form.insurance.healthGroupID?.trim()) && (
                    <div className="text-xs text-gray-600 mt-0.5 truncate line-clamp-1">
                      {[
                        form.insurance.healthMemberID &&
                          `Member ID: ${form.insurance.healthMemberID}`,
                        form.insurance.healthGroupID &&
                          `Group ID: ${form.insurance.healthGroupID}`,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </div>
                  )}
                </>
              }
            />

            <EvidenceRow
              id="review-ins-bi"
              title="Third-Party / BI"
              value={insuranceSubs.thirdParty}
              onChange={(v) => handleSubsChange('thirdParty', v)}
              contextPlaceholder=""
              hintLines={
                (form.insurance.defendants?.length || 0) > 0 && (
                  <div className="space-y-0.5 mt-0.5">
                    {form.insurance.defendants.map((d, idx) => {
                      const parts = [
                        d.insuranceCompanyName,
                        d.policyHolderName
                          ? `Holder: ${d.policyHolderName}`
                          : null,
                        d.policyNumber ? `Policy # ${d.policyNumber}` : null,
                        d.claimNumber ? `Claim # ${d.claimNumber}` : null,
                      ]
                        .filter(Boolean)
                        .join(' • ');
                      return (
                        <div
                          key={`${d.policyNumber || d.claimNumber || idx}`}
                          className="text-xs text-gray-600 truncate line-clamp-1"
                        >
                          {parts}
                        </div>
                      );
                    })}
                  </div>
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineReviewPanel;
